import { motion } from 'framer-motion'
import { Card } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { useServices } from '../../../state/services'
import { formatDistanceToNow } from 'date-fns'
import { Calendar, DollarSign, Eye, Zap } from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'

export default function MyServicesPage() {
  const { t } = useTranslation()
  const { userSubscriptions, dataIsLoading, dataError } = useServices()

  if (dataIsLoading) {
    return (
      <div className="p-6">
        <h1 className="text-3xl font-bold mb-6">{t('pages.myServices.title')}</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="p-6 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-2" />
              <div className="h-3 bg-muted rounded w-1/2 mb-4" />
              <div className="h-8 bg-muted rounded w-full" />
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (dataError) {
    return (
      <div className="p-6">
        <h1 className="text-3xl font-bold mb-6">{t('pages.myServices.title')}</h1>
        <Card className="p-6 text-center">
          <p className="text-red-600">{t('pages.myServices.failedToLoad')}</p>
        </Card>
      </div>
    )
  }

  if (!userSubscriptions || userSubscriptions.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-3xl font-bold mb-6">{t('pages.myServices.title')}</h1>
        <Card className="p-6 text-center">
          <p className="text-muted-foreground">{t('pages.myServices.noSubscriptions')}</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold mb-6">{t('pages.myServices.title')}</h1>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        {userSubscriptions.map((subscription, index) => (
          <motion.div
            key={subscription.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="group relative overflow-hidden border-0 bg-gradient-to-br from-card via-muted to-background shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <div className="relative p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-primary/20 to-primary/5 rounded-lg">
                      <Zap className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground">{subscription.service?.name.toUpperCase()}</h3>
                      <p className="text-sm text-muted-foreground">{t('pages.myServices.activeSubscription')}</p>
                    </div>
                  </div>
                  <Badge 
                    variant={subscription.status === 'active' ? 'default' : 'secondary'}
                    className={`px-3 py-1 text-xs font-semibold ${
                      subscription.status === 'active' 
                        ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                        : 'bg-muted text-muted-foreground border-border'
                    }`}
                  >
                    {subscription.status}
                  </Badge>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-2 text-muted-foreground">
                    <DollarSign className="w-4 h-4 text-primary" />
                    <span className="text-lg font-semibold text-foreground">{subscription.amount} {subscription.currency}</span>
                    <span className="text-sm text-muted-foreground">{t('pages.myServices.perSubscription')}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2 text-muted-foreground">
                    <Calendar className="w-4 h-4 text-primary" />
                    <span className="text-sm">
                      {t('pages.myServices.started')} {formatDistanceToNow(new Date(subscription.created_at), { addSuffix: true })}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-gradient-to-r from-primary to-primary-600 h-2 rounded-full w-full"></div>
                  </div>
                </div>

                <button className="mt-4 w-full flex items-center justify-center space-x-2 bg-primary/10 hover:bg-primary hover:text-primary-foreground text-primary border border-primary/30 hover:border-primary rounded-lg py-2.5 px-4 transition-all duration-200 group/btn">
                  <Eye className="w-4 h-4" />
                  <span className="font-medium">{t('pages.myServices.viewDetails')}</span>
                </button>
              </div>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </div>
  )
}